import React, { useEffect, useState, useRef } from 'react'
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Collapse,
  LinearProgress,
  useTheme,
  Paper
} from '@mui/material'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { WrapNav } from '../../_types/wrap-nave.type'

interface PremiumNavProps {
  items: WrapNav
}

const PremiumNav: React.FC<PremiumNavProps> = ({ items }) => {
  const [activeId, setActiveId] = useState<string | null>(null)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})
  const [scrollProgress, setScrollProgress] = useState(0)
  const observer = useRef<IntersectionObserver | null>(null)
  const theme = useTheme()

  // Initialize all sections as expanded
  useEffect(() => {
    const initial: Record<string, boolean> = {}
    items.sections.forEach(section => {
      initial[section.targetId] = true
    })
    setExpandedSections(initial)
  }, [items])

  // Track scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      const progress = (scrollTop / docHeight) * 100
      setScrollProgress(Math.min(progress, 100))
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // IntersectionObserver for scroll spy
  useEffect(() => {
    if (observer.current) {
      observer.current.disconnect()
    }

    observer.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id)
          }
        })
      },
      {
        root: null,
        rootMargin: '-100px 0px -50% 0px',
        threshold: 0.1
      }
    )

    const elements = items.sections.flatMap(item => [
      document.getElementById(item.targetId),
      ...item.subSections.map(sub => document.getElementById(sub.targetId))
    ])

    elements.forEach(element => {
      if (element) {
        observer.current?.observe(element)
      }
    })

    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
  }, [items])

  const handleClick = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      const top = element.getBoundingClientRect().top + window.pageYOffset - 100
      window.scrollTo({
        top: top,
        behavior: 'smooth'
      })
    }
  }

  const toggleSection = (targetId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [targetId]: !prev[targetId]
    }))
  }

  const isActive = (id: string) => activeId === id

  const isSectionActive = (section: typeof items.sections[0]) => {
    if (activeId === section.targetId) return true
    return section.subSections.some(sub => sub.targetId === activeId)
  }

  return (
    <Paper
      elevation={4}
      sx={{
        position: 'sticky',
        top: 80,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid rgba(226,90,11,0.2)'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #e25a0b 0%, #ff7b2e 100%)',
          p: 2
        }}
      >
        <Typography
          variant='subtitle1'
          fontWeight='bold'
          color='white'
          textAlign='center'
        >
          TABLE OF CONTENTS
        </Typography>
      </Box>

      {/* Scroll Progress */}
      <LinearProgress
        variant='determinate'
        value={scrollProgress}
        sx={{
          height: 3,
          '& .MuiLinearProgress-bar': {
            background: 'linear-gradient(90deg, #e25a0b 0%, #ff7b2e 100%)'
          }
        }}
      />

      {/* Navigation List */}
      <List sx={{ py: 1 }}>
        {items.sections.map((section, i) => (
          <React.Fragment key={`section-${i}`}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  handleClick(section.targetId)
                  if (section.subSections.length > 0) {
                    toggleSection(section.targetId)
                  }
                }}
                sx={{
                  py: 1,
                  px: 2,
                  borderLeft: isSectionActive(section)
                    ? '3px solid #e25a0b'
                    : '3px solid transparent',
                  backgroundColor: isSectionActive(section)
                    ? 'rgba(226,90,11,0.08)'
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(226,90,11,0.12)'
                  }
                }}
              >
                <ListItemText
                  primary={section.title}
                  primaryTypographyProps={{
                    fontWeight: isSectionActive(section) ? 'bold' : 'medium',
                    color: isSectionActive(section)
                      ? theme.palette.primary.main
                      : 'text.primary',
                    fontSize: '0.95rem'
                  }}
                />
                {section.subSections.length > 0 && (
                  expandedSections[section.targetId]
                    ? <ExpandLess fontSize='small' />
                    : <ExpandMore fontSize='small' />
                )}
              </ListItemButton>
            </ListItem>

            {section.subSections.length > 0 && (
              <Collapse
                in={expandedSections[section.targetId]}
                timeout='auto'
                unmountOnExit
              >
                <List component='div' disablePadding>
                  {section.subSections.map((sub, j) => (
                    <ListItem key={`sub-${i}-${j}`} disablePadding>
                      <ListItemButton
                        onClick={() => handleClick(sub.targetId)}
                        sx={{
                          py: 0.5,
                          pl: 4,
                          pr: 2,
                          borderLeft: isActive(sub.targetId)
                            ? '3px solid #e25a0b'
                            : '3px solid transparent',
                          backgroundColor: isActive(sub.targetId)
                            ? 'rgba(226,90,11,0.05)'
                            : 'transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(226,90,11,0.08)'
                          }
                        }}
                      >
                        <ListItemText
                          primary={`• ${sub.title}`}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: isActive(sub.targetId) ? 'bold' : 'normal',
                            color: isActive(sub.targetId)
                              ? theme.palette.primary.main
                              : 'text.secondary'
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    </Paper>
  )
}

export default PremiumNav
